import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = 'https://iia-backend-production.up.railway.app';

const BLOCK_TYPES = [
  { type: 'heading', label: '📝 Heading', icon: 'H' },
  { type: 'text', label: '📄 Text Block', icon: 'T' },
  { type: 'video_embed', label: '🎥 Video Embed', icon: 'V' },
  { type: 'image', label: '🖼️ Image URL', icon: 'I' },
  { type: 'file', label: '📎 File Link', icon: 'F' },
  { type: 'hyperlink', label: '🔗 Hyperlink', icon: 'L' },
];

const AdminTaskBuilder = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();

  const [task, setTask] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showBlockMenu, setShowBlockMenu] = useState(false);

  useEffect(() => {
    fetchTask();
  }, [taskId]);

  const fetchTask = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/instructional/tasks/${taskId}`
      );
      setTask(res.data.task);
      const content = res.data.task.content;
      if (content && content.blocks && content.blocks.length > 0) {
        setBlocks(content.blocks);
      }
    } catch (err) {
      console.error('Fetch task error:', err);
    } finally {
      setLoading(false);
    }
  };

  const addBlock = (type) => {
    const newBlock = { type, id: Date.now() };
    if (type === 'heading') newBlock.content = '';
    if (type === 'text') newBlock.content = '';
    if (type === 'video_embed') newBlock.url = '';
    if (type === 'image') { newBlock.url = ''; newBlock.alt = ''; }
    if (type === 'file') { newBlock.url = ''; newBlock.name = ''; }
    if (type === 'hyperlink') { newBlock.url = ''; newBlock.label = ''; }
    setBlocks([...blocks, newBlock]);
    setShowBlockMenu(false);
    setSaved(false);
  };

  const updateBlock = (index, field, value) => {
    const updated = [...blocks];
    updated[index] = { ...updated[index], [field]: value };
    setBlocks(updated);
    setSaved(false);
  };

  const moveBlock = (index, direction) => {
    const updated = [...blocks];
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= updated.length) return;
    [updated[index], updated[target]] = [updated[target], updated[index]];
    setBlocks(updated);
    setSaved(false);
  };

  const deleteBlock = (index) => {
    const updated = blocks.filter((_, i) => i !== index);
    setBlocks(updated);
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API_URL}/api/instructional/tasks/${taskId}/content`,
        { content: { blocks } }
      );
      setSaved(true);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderBlockEditor = (block, index) => {
    return (
      <div key={block.id || index} style={styles.blockCard}>
        {/* Block Controls */}
        <div style={styles.blockControls}>
          <span style={styles.blockTypeLabel}>
            {BLOCK_TYPES.find(b => b.type === block.type)?.label || block.type}
          </span>
          <div style={styles.blockActions}>
            <button
              style={styles.moveBtn}
              onClick={() => moveBlock(index, 'up')}
              disabled={index === 0}
            >↑</button>
            <button
              style={styles.moveBtn}
              onClick={() => moveBlock(index, 'down')}
              disabled={index === blocks.length - 1}
            >↓</button>
            <button
              style={styles.deleteBlockBtn}
              onClick={() => deleteBlock(index)}
            >✕</button>
          </div>
        </div>

        {/* Block Fields */}
        <div style={styles.blockFields}>
          {block.type === 'heading' && (
            <input
              style={styles.blockInput}
              placeholder="Enter heading text..."
              value={block.content || ''}
              onChange={e => updateBlock(index, 'content', e.target.value)}
            />
          )}

          {block.type === 'text' && (
            <textarea
              style={styles.blockTextarea}
              placeholder="Enter paragraph text..."
              value={block.content || ''}
              onChange={e => updateBlock(index, 'content', e.target.value)}
              rows={5}
            />
          )}

          {block.type === 'video_embed' && (
            <div>
              <input
                style={styles.blockInput}
                placeholder="Paste video embed URL (YouTube, Vimeo, Loom...)"
                value={block.url || ''}
                onChange={e => updateBlock(index, 'url', e.target.value)}
              />
              <p style={styles.hint}>
                For YouTube: use embed URL format — 
                https://www.youtube.com/embed/VIDEO_ID
              </p>
            </div>
          )}

          {block.type === 'image' && (
            <div style={styles.fieldGroup}>
              <input
                style={styles.blockInput}
                placeholder="Image URL"
                value={block.url || ''}
                onChange={e => updateBlock(index, 'url', e.target.value)}
              />
              <input
                style={styles.blockInput}
                placeholder="Alt text (description of image)"
                value={block.alt || ''}
                onChange={e => updateBlock(index, 'alt', e.target.value)}
              />
            </div>
          )}

          {block.type === 'file' && (
            <div style={styles.fieldGroup}>
              <input
                style={styles.blockInput}
                placeholder="File URL (link to PDF, doc, etc.)"
                value={block.url || ''}
                onChange={e => updateBlock(index, 'url', e.target.value)}
              />
              <input
                style={styles.blockInput}
                placeholder="Display name (e.g. Week 1 Workbook.pdf)"
                value={block.name || ''}
                onChange={e => updateBlock(index, 'name', e.target.value)}
              />
            </div>
          )}

          {block.type === 'hyperlink' && (
            <div style={styles.fieldGroup}>
              <input
                style={styles.blockInput}
                placeholder="URL (https://...)"
                value={block.url || ''}
                onChange={e => updateBlock(index, 'url', e.target.value)}
              />
              <input
                style={styles.blockInput}
                placeholder="Link label (e.g. Click here to join Zoom)"
                value={block.label || ''}
                onChange={e => updateBlock(index, 'label', e.target.value)}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Page Builder</span>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.returnBtn} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <button
            style={saving ? styles.saveBtnDisabled : 
              saved ? styles.saveBtnSaved : styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Content'}
          </button>
        </div>
      </div>

      <div style={styles.main}>
        {loading && (
          <div style={styles.centered}>
            <p style={styles.loadingText}>Loading task...</p>
          </div>
        )}

        {!loading && task && (
          <>
            {/* Task Info */}
            <div style={styles.taskInfo}>
              <h2 style={styles.taskTitle}>{task.title}</h2>
              <p style={styles.taskType}>
                {task.task_type} — Task ID {task.id}
              </p>
            </div>

            {/* Blocks */}
            <div style={styles.blocksContainer}>
              {blocks.length === 0 && (
                <div style={styles.emptyState}>
                  <p style={styles.emptyIcon}>📄</p>
                  <p style={styles.emptyText}>
                    No content blocks yet. Add your first block below.
                  </p>
                </div>
              )}

              {blocks.map((block, index) => renderBlockEditor(block, index))}
            </div>

            {/* Add Block */}
            <div style={styles.addBlockSection}>
              <button
                style={styles.addBlockBtn}
                onClick={() => setShowBlockMenu(!showBlockMenu)}
              >
                + Add Content Block
              </button>

              {showBlockMenu && (
                <div style={styles.blockMenu}>
                  {BLOCK_TYPES.map(bt => (
                    <button
                      key={bt.type}
                      style={styles.blockMenuItem}
                      onClick={() => addBlock(bt.type)}
                    >
                      {bt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Save Button Bottom */}
            <div style={styles.saveBottom}>
              <button
                style={saving ? styles.saveBtnDisabled :
                  saved ? styles.saveBtnSaved : styles.saveBtn}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Content'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    color: '#ffffff',
  },
  header: {
    backgroundColor: '#0f1a33',
    borderBottom: '1px solid #C49A28',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    color: '#C49A28',
    fontSize: '24px',
    fontWeight: '800',
    margin: '0',
    letterSpacing: '2px',
  },
  logoText: {
    color: '#ffffff80',
    fontSize: '14px',
    letterSpacing: '1px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  returnBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ffffff30',
    color: '#ffffff80',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  saveBtn: {
    backgroundColor: '#C49A28',
    color: '#1A2744',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  saveBtnDisabled: {
    backgroundColor: '#C49A2860',
    color: '#1A2744',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'not-allowed',
  },
  saveBtnSaved: {
    backgroundColor: '#1E8449',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
  },
  main: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '32px',
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    padding: '60px',
  },
  loadingText: {
    color: '#ffffff60',
    fontSize: '16px',
  },
  taskInfo: {
    marginBottom: '32px',
    paddingBottom: '24px',
    borderBottom: '1px solid #ffffff15',
  },
  taskTitle: {
    color: '#C49A28',
    fontSize: '24px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  taskType: {
    color: '#ffffff40',
    fontSize: '13px',
    margin: '0',
  },
  blocksContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px',
  },
  emptyState: {
    backgroundColor: '#0f1a33',
    border: '2px dashed #ffffff20',
    borderRadius: '12px',
    padding: '48px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: '32px',
    margin: '0 0 12px 0',
  },
  emptyText: {
    color: '#ffffff40',
    fontSize: '15px',
    margin: '0',
  },
  blockCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    overflow: 'hidden',
  },
  blockControls: {
    backgroundColor: '#0d1628',
    borderBottom: '1px solid #ffffff10',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  blockTypeLabel: {
    color: '#C49A28',
    fontSize: '12px',
    fontWeight: '600',
  },
  blockActions: {
    display: 'flex',
    gap: '6px',
  },
  moveBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ffffff20',
    color: '#ffffff60',
    width: '28px',
    height: '28px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  deleteBlockBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ff000030',
    color: '#ff6666',
    width: '28px',
    height: '28px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  blockFields: {
    padding: '16px',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  blockInput: {
    width: '100%',
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff20',
    borderRadius: '6px',
    padding: '10px 14px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  blockTextarea: {
    width: '100%',
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff20',
    borderRadius: '6px',
    padding: '10px 14px',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    lineHeight: '1.6',
    boxSizing: 'border-box',
  },
  hint: {
    color: '#ffffff40',
    fontSize: '12px',
    margin: '8px 0 0 0',
  },
  addBlockSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '32px',
  },
  addBlockBtn: {
    backgroundColor: 'transparent',
    border: '2px dashed #C49A2860',
    color: '#C49A28',
    borderRadius: '8px',
    padding: '14px 32px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
  },
  blockMenu: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
  },
  blockMenuItem: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ffffff',
    padding: '12px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    textAlign: 'left',
    fontWeight: '500',
  },
  saveBottom: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: '16px',
    borderTop: '1px solid #ffffff10',
  },
};

export default AdminTaskBuilder;